import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { JobProfileVersions } from '~/shared/data-contract/job-profile-versions';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

export const VERSION_NOTE_LOCKED_ERROR_CODE = 'JSA0013';

type EditVersionNoteVariables = {
    readonly architectureId: string;
    readonly comment: string | null;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly version: string;
};

type EditVersionNoteContext = {
    readonly previousVersions: JobProfileVersions | undefined;
    readonly versionsQueryKey: readonly unknown[];
};

export type UseEditVersionNoteResult = UseMutationResult<
    unknown,
    Error,
    EditVersionNoteVariables,
    EditVersionNoteContext
>;

const hasErrorCodes = (value: unknown): value is { readonly errors: readonly { readonly errorCode?: unknown }[] } => {
    if (!is.object(value)) {
        return false;
    }
    const candidate: { readonly errors?: unknown } = value;
    return is.array(candidate.errors);
};

const hasErrorCode = (value: unknown): value is { readonly errorCode: string } => {
    if (!is.object(value)) {
        return false;
    }
    const candidate: { readonly errorCode?: unknown } = value;
    return is.string(candidate.errorCode);
};

export const isVersionNoteLockedError = (error: unknown): boolean => {
    if (hasErrorCode(error) && error.errorCode === VERSION_NOTE_LOCKED_ERROR_CODE) {
        return true;
    }
    if (hasErrorCodes(error)) {
        return error.errors.some(
            (entry) => is.string(entry.errorCode) && entry.errorCode === VERSION_NOTE_LOCKED_ERROR_CODE,
        );
    }
    return false;
};

const versionsQueryKeyFor = (
    architectureId: string,
    itemId: string,
    itemType: 'job-level' | 'job-role',
): readonly unknown[] =>
    itemType === 'job-level'
        ? jobArchitectureKeys.levelVersions(architectureId, itemId)
        : jobArchitectureKeys.roleVersions(architectureId, itemId);

const detailQueryKeyFor = (
    architectureId: string,
    itemId: string,
    itemType: 'job-level' | 'job-role',
): readonly unknown[] =>
    itemType === 'job-level'
        ? jobArchitectureKeys.level(architectureId, itemId)
        : jobArchitectureKeys.role(architectureId, itemId);

export const useEditVersionNote = (): UseEditVersionNoteResult => {
    const { editJobLevelVersionNote, editJobRoleVersionNote } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const { showErrorMessageSnackbar, showErrorSnackbar } = useSnackbars();

    return useMutation<unknown, Error, EditVersionNoteVariables, EditVersionNoteContext>({
        mutationFn: async ({ architectureId, comment, itemId, itemType, version }) => {
            const result =
                itemType === 'job-level'
                    ? await editJobLevelVersionNote({
                          comment,
                          jobArchitectureId: architectureId,
                          jobLevelId: itemId,
                          version,
                      })
                    : await editJobRoleVersionNote({
                          comment,
                          jobArchitectureId: architectureId,
                          jobRoleId: itemId,
                          version,
                      });

            if (result.isErr === true) {
                throw result.error;
            }

            return result.value;
        },
        onMutate: async ({ architectureId, comment, itemId, itemType, version }) => {
            const versionsQueryKey = versionsQueryKeyFor(architectureId, itemId, itemType);
            const detailQueryKey = detailQueryKeyFor(architectureId, itemId, itemType);

            await Promise.all([
                queryClient.cancelQueries({ queryKey: versionsQueryKey }),
                queryClient.cancelQueries({ queryKey: detailQueryKey }),
            ]);

            const previousVersions = queryClient.getQueryData<JobProfileVersions>(versionsQueryKey);

            queryClient.setQueryData<JobProfileVersions>(versionsQueryKey, (old) => {
                if (is.nullOrUndefined(old)) {
                    return old;
                }

                return old.map((versionItem) => {
                    if (versionItem.version === Number(version)) {
                        return { ...versionItem, comment };
                    }
                    return versionItem;
                });
            });

            return { previousVersions, versionsQueryKey };
        },
        onError: (error, _variables, context) => {
            if (!is.undefined(context)) {
                queryClient.setQueryData(context.versionsQueryKey, context.previousVersions);
            }

            if (isVersionNoteLockedError(error)) {
                showErrorMessageSnackbar('pages.job-architecture-v2.version-note.locked-toast');
                return;
            }

            showErrorSnackbar(error);
        },
        onSettled: (_data, _error, { architectureId, itemId, itemType }) => {
            const versionsQueryKey = versionsQueryKeyFor(architectureId, itemId, itemType);
            const detailQueryKey = detailQueryKeyFor(architectureId, itemId, itemType);

            queryClient.invalidateQueries({ queryKey: versionsQueryKey });
            queryClient.invalidateQueries({ queryKey: detailQueryKey });
            queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.architecture(architectureId) });
            queryClient.invalidateQueries({ queryKey: jobArchitectureKeys.architectureDetail(architectureId) });
        },
    });
};
